/**
 * UserConfig 탭별 동작 요약
 *
 * - 탭 전환은 단순 UI 상태(activeTab)로 표시할 컴포넌트를 바꿈
 * - `retrieveUserConfig`로 공통 설정을 한 번 로드하고 로컬 상태(userConfig)에 저장함
 *
 * 공통 설정 탭
 * - `adjust_config_global`, `item_list_column_config` 탭은 각각 독립 폼을 사용함
 * - `AdjustConfigTab`은 가격조정 관련 설정만, `ItemListColumnConfigTab`은 컬럼 설정만 저장함
 * - 저장 시 `updateUserConfig`를 동일 API로 호출하나, 수정된 부분만 payload로 전송함
 *
 * 독립 탭
 * - `schedule_global`, `other_settings`, `user_profile`, `user_password`, `shop_login_info`, `item_transfer` 탭은 각각 독립 폼과 API 호출을 가짐
 * - `UserScheduleForm`: `fetchUserSchedules`, `createUserSchedule`, `updateUserSchedule`, `deleteUserSchedule`
 * - `UserProfileForm`: `retrieveUserProfile`, `updateUserProfile`
 * - `UserPasswordForm`: `updateUserPassword`
 * - `ShopLoginInfoForm`: `fetchShopLoginInfos`, `createShopLoginInfo`, `updateShopLoginInfo`, `deleteShopLoginInfo`
 */
import React from 'react';
import { Link, useSearchParams } from 'react-router';

import { useAuth } from "../../../features/auth/AuthContext";
import { canAccessItemTransfer } from '../../items/itemImportAccess';
import { retrieveUserConfig, updateUserConfig } from '../../../api/account';
import { normalizeItemListColumnPreferences } from '../../items/model/item-list.types';
import { UserConfigInputs, UserConfigInputsDefault, normalizeReprocessWaitSettings } from '../model/user-config.types';
import { AdjustConfigTab, ItemListColumnConfigTab } from '../components/UserSettingsConfigTabs';
import ItemReprocessWaitTimeForm from '../components/ItemReprocessWaitTimeForm';
import ItemTransferManager from '../components/ItemTransferManager';
import UserScheduleForm from '../components/UserScheduleForm';
import UserProfileForm from '../components/UserProfileForm';
import UserPasswordForm from '../components/UserPasswordForm';
import ShopLoginInfoForm from '../components/ShopLoginInfoForm';
import ShopApiSettingsForm from '../../shops/components/ShopApiSettingsForm';
import Tabs, { Tab } from '../../../shared/ui/Tabs';


const UserSettingsPage = () => {

    const auth = useAuth();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = React.useState(true);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [activeTab, setActiveTab] = React.useState<
        'adjust_config_global' |
        'schedule_global' |
        'other_settings' |
        'item_list_column_config' |
        'shops' |
        'shop_login_info' |
        'user_profile' |
        'user_password' |
        'item_transfer'
    >('shops');

    const [userConfig, setUserConfig] = React.useState<UserConfigInputs | null>(null);
    const [reprocessMinimum, setReprocessMinimum] = React.useState(3);
    const [reprocessMinimumLoading, setReprocessMinimumLoading] = React.useState(false);

    const transferAllowed = canAccessItemTransfer(auth);
    const requestedTab = searchParams.get('tab');
    React.useEffect(() => {
        if (requestedTab === 'shops') {
            setActiveTab('shops');
        } else if (requestedTab === 'item_transfer' && transferAllowed) {
            setActiveTab('item_transfer');
        } else if (!transferAllowed) {
            setActiveTab((current) => current === 'item_transfer' ? 'shops' : current);
        }
    }, [requestedTab, transferAllowed]);

    React.useEffect(() => {
        retrieveUserConfig({
            auth,
            setErrorMessage: setErrorMessage,
            setResult: (data: any) => {
                const reprocessWait = normalizeReprocessWaitSettings(data);
                const adjustConfig = (data.adjust_config && Object.keys(data.adjust_config).length > 0)
                    ? data.adjust_config
                    : UserConfigInputsDefault.adjust_config;

                const itemListColumnPreferences = normalizeItemListColumnPreferences(
                    data.item_list_column_config,
                );

                setReprocessMinimum(reprocessWait.minimum);
                setUserConfig({
                    adjust_config: adjustConfig,
                    adjust_no_exposed: data.adjust_no_exposed,
                    skip_low_coupon: data.skip_low_coupon,
                    adjust_max_step: data.adjust_max_step,
                    reset_max_step: data.reset_max_step,
                    reset_on_ll: data.reset_on_ll,
                    reset_on_ll_countime: data.reset_on_ll_countime,
                    should_recover_price_on_active_schedule: data.should_recover_price_on_active_schedule
                        ?? UserConfigInputsDefault.should_recover_price_on_active_schedule,
                    item_reprocess_wait_minutes: reprocessWait.value,
                    item_list_column_config: itemListColumnPreferences
                });

                setLoading(false);
            },
        });
    }, [auth]);

    const refreshReprocessWaitConfig = () => {
        setReprocessMinimumLoading(true);
        void retrieveUserConfig({
            auth,
            setErrorMessage,
            setResult: (data: any) => {
                const reprocessWait = normalizeReprocessWaitSettings(data);
                setReprocessMinimum(reprocessWait.minimum);
                setUserConfig((prev) => prev ? {
                    ...prev,
                    item_reprocess_wait_minutes: reprocessWait.value,
                } : prev);
            },
        }).finally(() => setReprocessMinimumLoading(false));
    };

    const updateConfig = async (
        payload: Partial<UserConfigInputs>,
        setErrorMessage: (message: string | null) => void,
        setProcessMessage: (message: string | null) => void,
        setSuccessMessage: (message: string | null) => void
    ) => {
        await updateUserConfig({
            auth,
            payload,
            setErrorMessage,
            setProcessMessage,
            setSuccessMessage,
            setResult: () => {
                setUserConfig((prev) => (prev ? { ...prev, ...payload } : prev));
            },
        });
    };

    if (loading || !userConfig) return <div>로딩 중...</div>;

    return <>

        {errorMessage && (
            <div className="alert alert-danger" role="alert">
                {errorMessage}
            </div>
        )}

        <div className="page-head page-head-standard d-flex justify-content-between align-items-center mb-0">
            <div>
                <p className="fs-5">
                    환경 설정
                </p>
            </div>
            <div>
                <Link to="/" className="btn btn-outline-secondary">뒤로가기</Link>
            </div>
        </div>

        <div className="settings-card">
        <Tabs
            activeKey={activeTab}
            onSelect={(key) => {
                if (!key) return;
                setActiveTab(key as typeof activeTab);
                if (key === 'other_settings') refreshReprocessWaitConfig();
            }}
            ariaLabel="환경 설정"
            className="settings-tabs-ui"
        >
            <Tab eventKey="shops" title="상점 API 설정">
                <ShopApiSettingsForm />
            </Tab>
            <Tab eventKey="shop_login_info" title="상점 로그인 설정">
                <ShopLoginInfoForm auth={auth} />
            </Tab>
            <Tab eventKey="adjust_config_global" title="가격조정 전역 설정">
                <AdjustConfigTab userConfig={userConfig} updateConfig={updateConfig} />
            </Tab>
            <Tab eventKey="schedule_global" title="스케줄 전역 설정">
                <UserScheduleForm auth={auth} />
            </Tab>
            <Tab eventKey="item_list_column_config" title="상품목록 컬럼 설정">
                <ItemListColumnConfigTab userConfig={userConfig} updateConfig={updateConfig} />
            </Tab>
            <Tab eventKey="user_profile" title="사용자 프로필">
                <UserProfileForm auth={auth} />
            </Tab>
            <Tab eventKey="user_password" title="비밀번호 변경">
                <UserPasswordForm auth={auth} />
            </Tab>
            {transferAllowed && <Tab eventKey="item_transfer" title="상품목록 세트">
                <ItemTransferManager />
            </Tab>}
            <Tab eventKey="other_settings" title="기타 설정">
                {reprocessMinimumLoading ? <div>로딩 중...</div> : (
                    <ItemReprocessWaitTimeForm
                        auth={auth}
                        value={userConfig.item_reprocess_wait_minutes}
                        minimum={reprocessMinimum}
                        onSaved={(value) => setUserConfig((prev) => (
                            prev ? { ...prev, item_reprocess_wait_minutes: value } : prev
                        ))}
                    />
                )}
            </Tab>
        </Tabs>
        </div>

    </>
}

export default UserSettingsPage;
